/**
 * Agents Context with modern React patterns
 * Implementação conforme ADR-004: Context + localStorage + Background Refresh
 */

import React, { useEffect, useState, useCallback, useRef } from 'react'
import AgentsContext from './AgentsContext'
import { agentsService } from '../../services/agentsService'
import { useAuth } from '../auth'
import type { AgentsContextType, AgentUI } from '../../types/agents'

interface AgentsProviderProps {
  children: React.ReactNode
}

export function AgentsProvider({ children }: AgentsProviderProps) {
  const [agents, setAgents] = useState<AgentUI[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const { isAuthenticated, permissions } = useAuth()

  // Track if agents are already loading to prevent duplicate calls
  const loadingRef = useRef(false)

  /**
   * Load agents with cache strategy
   */
  const loadAgents = useCallback(
    async (force = false) => {
      if (!isAuthenticated) {
        setAgents([])
        setIsLoading(false)
        loadingRef.current = false
        return
      }

      // Prevent duplicate calls (React StrictMode double-render)
      if (loadingRef.current) return
      loadingRef.current = true

      try {
        setError(null)
        if (force) setIsLoading(true)

        // Pass user permissions to enable proper cache invalidation
        const userPermissions = permissions?.allowedAgents || []
        const agentsData = force
          ? await agentsService.refreshAgents(userPermissions)
          : await agentsService.getAgents(userPermissions)

        setAgents(agentsData)
        setLastUpdated(new Date())
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Erro desconhecido'

        // Keep previous data if available, show error but don't clear cache
        setError(errorMsg)
      } finally {
        setIsLoading(false)
        loadingRef.current = false
      }
    },
    [isAuthenticated, permissions?.allowedAgents]
  )

  /**
   * Force refresh agents
   */
  const refreshAgents = useCallback(async () => {
    await loadAgents(true)
  }, [loadAgents])

  // Load agents on mount and auth changes
  useEffect(() => {
    loadAgents()
  }, [loadAgents])

  // Clear cache on logout
  useEffect(() => {
    if (!isAuthenticated) {
      agentsService.clearCache()
      setAgents([])
      setLastUpdated(null)
    }
  }, [isAuthenticated])

  const contextValue: AgentsContextType = {
    agents,
    isLoading,
    error,
    lastUpdated,
    refreshAgents,
  }

  return (
    <AgentsContext.Provider value={contextValue}>
      {children}
    </AgentsContext.Provider>
  )
}
